import { toast } from 'sonner'

import type { Refused } from '@/lib/sync-manager/push-to-server'
import type { InterventoDoc } from '@/db/local/dexie-db'

export interface RefusalNotice {
  interventoId: string
  title: string
  description: string
}

/**
 * What to tell the user about the events a push had refused: one notice per
 * intervento, about the most important thing lost. The device already shows
 * what the server has (its row came back with the answer); the notice says why
 * the screen changed.
 */
export function refusalNotices(
  rows: readonly InterventoDoc[],
  refused: readonly Refused[]
): RefusalNotice[] {
  const refusedIds = new Set(
    refused.filter((item) => item.type === 'evento').map((item) => item.id)
  )
  if (refusedIds.size === 0) return []

  return rows.flatMap((row) => {
    const lost = (row.eventi ?? []).filter((event) => refusedIds.has(event.id))
    if (!lost.length) return []
    const name = row.id_interno ?? ''

    const repairEvents = new Set([
      'riparazione_avviata',
      'riparazione_completata',
      'riparazione_annullata',
    ])
    if (lost.some((event) => repairEvents.has(event.tipo))) {
      return {
        interventoId: row.id,
        title: `Riparazione non registrata (${name})`,
        description:
          'Il server non l’ha accettata: lo Storico mostra cosa risulta registrato. Le foto della riparazione restano sul telefono.',
      }
    }
    // A correction naming a repair changes that repair; one naming none, the
    // inspection
    if (lost.some((event) => event.riparazione_id !== null)) {
      return {
        interventoId: row.id,
        title: `Modifica della riparazione non registrata (${name})`,
        description:
          'Il server non l’ha accettata: lo Storico mostra la riparazione come risulta registrata.',
      }
    }
    if (lost.some((event) => event.tipo === 'correzione')) {
      return {
        interventoId: row.id,
        title: `Modifica dell’esito non registrata (${name})`,
        description:
          'Una riparazione è già stata avviata: l’esito dell’intervento può cambiarlo solo un amministratore.',
      }
    }
    return {
      interventoId: row.id,
      title: `Intervento ${name} non aggiornato`,
      description:
        'Il server non ha accettato il completamento: completalo di nuovo indicando l’esito.',
    }
  })
}

/** Shows the notices: they stay a while, the user may be looking elsewhere. */
export function showRefusalNotices(notices: readonly RefusalNotice[]): void {
  for (const notice of notices) {
    toast.warning(notice.title, {
      id: `rifiutato-${notice.interventoId}`,
      description: notice.description,
      duration: 15_000,
    })
  }
}
